
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, FileText, Shield } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth-provider";
import { useIsMobile } from "@/hooks/use-mobile";
import { LegalDocument } from "@/hooks/useLegalDocuments";

interface TermsDialogProps {
  showTerms: boolean;
  setShowTerms: (show: boolean) => void;
  isSubmitting: boolean;
  onConfirm: () => Promise<void>;
}

const TermsDialog = ({ showTerms, setShowTerms, isSubmitting, onConfirm }: TermsDialogProps) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { loading: loadingTerms, currentSetting: termsSetting } = useAppSettings('terms_and_conditions');
  const { loading: loadingPrivacy, currentSetting: privacySetting } = useAppSettings('privacy_policy');
  const [termsVersion, setTermsVersion] = useState<string>('');
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  
  console.log('[TERMS-DIALOG] Component rendered:', {
    showTerms,
    isSubmitting,
    loadingTerms,
    loadingPrivacy,
    hasTermsSetting: !!termsSetting,
    hasPrivacySetting: !!privacySetting,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    console.log('[TERMS-DIALOG] Terms setting changed:', {
      hasValue: !!termsSetting?.value,
      valueLength: termsSetting?.value?.length || 0,
      settingKey: termsSetting?.key
    });

    // Generate a version identifier based on the terms content
    if (termsSetting?.value) {
      try {
        const version = btoa(termsSetting.value).slice(0, 20);
        console.log('[TERMS-DIALOG] Generated terms version:', version);
        setTermsVersion(version);
      } catch (error) {
        console.error('[TERMS-DIALOG] Error generating terms version:', error);
        setTermsVersion('fallback-v1');
      }
    }
  }, [termsSetting]);

  useEffect(() => {
    console.log('[TERMS-DIALOG] Privacy setting changed:', {
      hasValue: !!privacySetting?.value,
      valueLength: privacySetting?.value?.length || 0,
      settingKey: privacySetting?.key
    });
  }, [privacySetting]);

  // Fetch active legal documents
  useEffect(() => {
    const fetchLegalDocuments = async () => {
      try {
        const { data, error } = await supabase
          .from('legal_documents')
          .select('*')
          .eq('is_active', true)
          .order('display_name');

        if (error) {
          console.error('[TERMS-DIALOG] Error fetching legal documents:', error);
        } else {
          setLegalDocuments(data || []);
        }
      } catch (error) {
        console.error('[TERMS-DIALOG] Error fetching legal documents:', error);
      } finally {
        setLoadingDocuments(false);
      }
    };

    if (showTerms) {
      fetchLegalDocuments();
    }
  }, [showTerms]);

  const handleAcceptTerms = async () => {
    console.log('[TERMS-DIALOG] Accept terms clicked:', {
      userId: user?.id,
      termsVersion,
      hasUser: !!user,
      hasTermsVersion: !!termsVersion
    });

    if (user && termsVersion) {
      try {
        console.log('[TERMS-DIALOG] Recording terms acceptance...');
        // Record the terms acceptance
        const { data, error } = await supabase.from('terms_acceptance').insert({
          user_id: user.id,
          terms_version: termsVersion
        });
        
        if (error) {
          console.error('[TERMS-DIALOG] Terms acceptance error:', error);
        } else {
          console.log('[TERMS-DIALOG] Terms acceptance recorded successfully');
        }
      } catch (error) {
        console.error('[TERMS-DIALOG] Failed to record terms acceptance:', error);
      }
    }
    
    setShowTerms(false);
    await onConfirm();
  };

  const handleDecline = () => {
    setShowTerms(false);
  };

  const handleDocumentClick = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  return (
    <Dialog open={showTerms} onOpenChange={setShowTerms}>
      <DialogContent className={`glass-effect max-w-2xl max-h-[85vh] flex flex-col ${isMobile ? 'mx-4 my-8' : ''}`}>
        <DialogHeader className="space-y-3 pb-4 border-b border-border/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl font-medium text-card-foreground">
                Terms & Conditions
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Please review and accept our terms to continue
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {loadingTerms || loadingPrivacy ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-3 text-sm text-muted-foreground">Loading terms...</span>
            </div>
          ) : (
            <>
              {termsSetting?.value && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <h4 className="font-medium text-card-foreground">Terms and Conditions</h4>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/20">
                    <div 
                      dangerouslySetInnerHTML={{ __html: termsSetting.value }} 
                      className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert [&>*]:mb-3 [&>*:last-child]:mb-0" 
                    />
                  </div>
                </div>
              )}
              
              {privacySetting?.value && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    <h4 className="font-medium text-card-foreground">Privacy Policy</h4>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/20">
                    <div 
                      dangerouslySetInnerHTML={{ __html: privacySetting.value }} 
                      className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert [&>*]:mb-3 [&>*:last-child]:mb-0" 
                    />
                  </div>
                </div>
              )}

              {!loadingDocuments && legalDocuments.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-primary" />
                    <h4 className="font-medium text-card-foreground">Additional Documents</h4>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/20">
                    <div className="grid gap-2">
                      {legalDocuments.map((document) => (
                        <button
                          key={document.id}
                          onClick={() => handleDocumentClick(document.file_url)}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 transition-colors text-left group"
                        >
                          <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-card-foreground group-hover:text-primary transition-colors">
                            {document.display_name}
                          </span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {!termsSetting?.value && !privacySetting?.value && !loadingTerms && !loadingPrivacy && (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    By continuing, you agree to our service conditions for parcel delivery.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-4 border-t border-border/20">
          <div className="flex gap-3 w-full">
            <Button 
              onClick={handleDecline}
              variant="outline"
              className="flex-1"
              disabled={isSubmitting || loadingTerms || loadingPrivacy}
            >
              Decline
            </Button>
            <Button 
              onClick={handleAcceptTerms} 
              disabled={isSubmitting || loadingTerms || loadingPrivacy}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Accept & Continue'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsDialog;
